/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';

import { Grid, Link, PageGridContainer, Typography2, breakpoints } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import type { ImageWithMetadataFieldsFragment } from 'bundles/front-page/__generated__/contentfulTypes';

import AmazonLogo from 'images/front-page/logo-Amazon.png';
import DeepLearningAILogo from 'images/front-page/logo-DeepLearningAI.png';
import MetaLogo from 'images/front-page/logo-Meta.png';
import MicrosoftLogo from 'images/front-page/logo-Microsoft.png';

import _t from 'i18n!nls/front-page';

export const getPartnerLogosData = (partnerLogos: ImageWithMetadataFieldsFragment[]) => {
  return [
    {
      ...partnerLogos.find((logo) => logo.relationSlug === 'google'),
      url: '/search?partners=Google',
    },
    {
      imageAltText: 'DeepLearning.AI',
      image: {
        url: DeepLearningAILogo,
      },
      relationSlug: 'deeplearning-ai',
      url: '/search?partners=DeepLearning.AI',
    },
    {
      ...partnerLogos.find((logo) => logo.relationSlug === 'stanford'),
      url: '/search?partners=Stanford%20University',
    },
    {
      ...partnerLogos.find((logo) => logo.relationSlug === 'ibm-skills-network'),
      url: '/search?partners=IBM',
    },
    {
      ...partnerLogos.find((logo) => logo.relationSlug === 'imperial'),
      url: '/search?partners=Imperial+College+London',
    },
    {
      ...partnerLogos.find((logo) => logo.relationSlug === 'duke'),
      url: '/search?partners=Duke+University',
    },
    {
      ...partnerLogos.find((logo) => logo.relationSlug === 'penn'),
      url: '/search?partners=University+of+Pennsylvania',
    },
    {
      imageAltText: 'Meta',
      image: {
        url: MetaLogo,
      },
      relationSlug: 'meta',
      url: '/search?partners=meta',
    },
    {
      imageAltText: 'Amazon Web Services',
      image: {
        url: AmazonLogo,
      },
      relationSlug: 'aws',
      url: '/search?partners=Amazon%20Web%20Services',
    },
    {
      imageAltText: 'Microsoft',
      image: {
        url: MicrosoftLogo,
      },
      relationSlug: 'microsoft',
      url: '/search?partners=Microsoft',
    },
  ];
};

export const EXCLUDED_PARTNER_LOGO_SLUG = 'deeplearning-ai';

const PARTNER_LOGO_MAX_HEIGHT = 25;
const PARTNER_LOGO_MAX_WIDTH = 80;

type PartnerLogoSize = {
  [key: string]: number;
};

const CUSTOM_PARTNER_LOGO_WIDTHS: PartnerLogoSize = {
  google: 72,
  duke: 58,
  'ibm-skills-network': 56,
  'deeplearning-ai': 65,
  stanford: 84,
  microsoft: 46,
  imperial: 96,
  meta: 85,
  penn: 76,
  aws: 42,
};

const CUSTOM_PARTNER_LOGO_HEIGHT: PartnerLogoSize = {
  google: 24,
  duke: 20,
  'ibm-skills-network': 22,
  'deeplearning-ai': 35,
  stanford: 18,
  microsoft: 35,
  imperial: 25,
  meta: 32,
  penn: 25,
  aws: 25,
};

const styles = {
  section: css`
    margin-bottom: var(--cds-spacing-50);
    ${breakpoints.down('md')} {
      margin-bottom: var(--cds-spacing-400);
    }
  `,
  container: css`
    ${breakpoints.up('lg')} {
      padding: var(--cds-spacing-200) var(--cds-spacing-400) !important;
    }

    margin-top: var(--cds-spacing-200);
  `,
  mobileContainer: css`
    text-align: center;
    width: 100%;
    margin: 0 auto;
  `,
  title: css`
    display: flex;
    align-items: center;
    ${breakpoints.down('md')} {
      justify-content: center;
    }
  `,
  titleLink: css`
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  `,
  logos: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--cds-spacing-200);
    justify-content: space-between;
    flex-wrap: wrap;
    ${breakpoints.up('lg')} {
      margin-top: calc(var(--cds-spacing-100) * -1);
      margin-bottom: calc(var(--cds-spacing-100) * -1);
    }
    ${breakpoints.between('sm', 'md')} {
      margin-top: var(--cds-spacing-200);
    }
    ${breakpoints.down('md')} {
      display: none;
    }
  `,
  logosMobile: css`
    ${breakpoints.up('lg')} {
      display: none;
    }

    margin-top: var(--cds-spacing-300);
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--cds-spacing-200);
    position: relative;
  `,
  partnerLogo: css`
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
    ${breakpoints.down('md')} {
      height: 100%;
      width: 18%;
      display: flex;
      align-items: center;
      padding: 0;

      img {
        max-width: 100% !important;
        height: auto;
        display: block;
        margin: 0 auto;
      }
    }
  `,
  border: css`
    // select the first child div of PageGridContainer
    > div:first-child {
      border-top: 1px solid var(--cds-color-grey-200);
    }
  `,
};

type Props = {
  partnerLogos: ImageWithMetadataFieldsFragment[];
};

const PartnerLogosBar = ({ partnerLogos }: Props) => {
  const logos = getPartnerLogosData(partnerLogos);
  const track = useTracker();

  const visibilityTrackerRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'partner_logo_list',
      },
    }
  );

  const trackClick = (linkTarget: string) => () => {
    track('click_button', {
      button: { name: 'partner_logo', linkName: 'partner_logo_link', linkURL: linkTarget, linkType: 'internal' },
      pageSection: {
        sectionName: 'partner_logo_list',
      },
    });
  };

  const getPartnerLogos = (excludedRelationSlug?: string) => {
    return logos.map(({ image, imageAltText, relationSlug, url: linkTarget }) => {
      const url = image?.url;

      if (!url || relationSlug === excludedRelationSlug) {
        return null;
      }

      return (
        <Link
          css={styles.partnerLogo}
          href={linkTarget}
          data-id={relationSlug}
          key={relationSlug}
          onClick={trackClick(linkTarget)}
        >
          <Imgix
            alt={imageAltText ?? ''}
            src={url}
            maxWidth={(relationSlug && CUSTOM_PARTNER_LOGO_WIDTHS[relationSlug]) || PARTNER_LOGO_MAX_WIDTH}
            maxHeight={(relationSlug && CUSTOM_PARTNER_LOGO_HEIGHT[relationSlug]) || PARTNER_LOGO_MAX_HEIGHT}
          />
        </Link>
      );
    });
  };

  return (
    <section css={styles.section} ref={visibilityTrackerRef}>
      <PageGridContainer css={styles.border}>
        <Grid container item xs={12} spacing={0} css={styles.container}>
          <Grid item xs={12} lg={2} css={styles.title}>
            <Link href="/partners" css={styles.titleLink} onClick={trackClick('/partners')}>
              <Typography2 variant="subtitleMedium" component="h2">
                {_t('Learn from 350+ organizations')}
              </Typography2>
            </Link>
          </Grid>
          {/* render 9 logos when viewport >= 1440px */}
          <Grid item md={12} lg={10} css={styles.logos}>
            {getPartnerLogos(EXCLUDED_PARTNER_LOGO_SLUG)}
          </Grid>
          {/* render 10 logos in two rows when viewport < 1440px */}
          <Grid item xs={12} css={styles.logosMobile}>
            {getPartnerLogos().slice(0, 5)}
          </Grid>
          <Grid item xs={12} css={styles.logosMobile}>
            {getPartnerLogos().slice(5)}
          </Grid>
        </Grid>
      </PageGridContainer>
    </section>
  );
};

export default PartnerLogosBar;
